import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Package, Check, BookOpen, FileText, Percent, TrendingUp, Play, Calendar, CalendarClock, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import CurrencyToggle from '../CurrencyToggle';
import LoginModal from '../LoginModal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FormationDetailsPage = () => {
  const { formationId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { fmt, currency } = useCurrency();
  const [formation, setFormation] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sobre');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingModality, setPendingModality] = useState(null);

  const fetchFormation = useCallback(async () => {
    try {
      const [formationRes, coursesRes] = await Promise.all([
        axios.get(`${API}/bundles/${formationId}`),
        axios.get(`${API}/courses`)
      ]);
      setFormation(formationRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar formação');
    } finally {
      setLoading(false);
    }
  }, [formationId]);

  useEffect(() => {
    fetchFormation();
  }, [fetchFormation]);

  const handleEnroll = (modality) => {
    if (!isAuthenticated) {
      setPendingModality(modality);
      setShowLoginModal(true);
      toast.info('Crie sua conta ou faça login para se matricular');
      return;
    }
    navigate(`/checkout/${formationId}/${modality}`);
  };

  // Após login/cadastro, segue direto para o checkout da modalidade escolhida
  useEffect(() => {
    if (isAuthenticated && pendingModality) {
      const modality = pendingModality;
      setPendingModality(null);
      setShowLoginModal(false);
      navigate(`/checkout/${formationId}/${modality}`);
    }
  }, [isAuthenticated, pendingModality, formationId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }

  if (!formation) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Formação não encontrada</div>
      </div>
    );
  }

  const includedCourses = courses.filter(c => formation.course_ids.includes(c.id));

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="mr-2" size={20} />
          Voltar
        </Button>

        {/* Seletor de país/moeda */}
        <div className="mb-6 bg-zinc-900/60 border border-zinc-800 rounded-xl p-4">
          <CurrencyToggle />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div>
              <Badge className="bg-red-600 text-white mb-4">{formation.code}</Badge>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {formation.title}
              </h1>
              <p className="text-gray-300 text-lg">{formation.description}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-center">
                <Clock className="text-red-500 mx-auto mb-2" size={24} />
                <div className="text-2xl font-bold text-white">{formation.total_hours}h</div>
                <div className="text-xs text-gray-400">Carga Horária</div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-center">
                <Package className="text-red-500 mx-auto mb-2" size={24} />
                <div className="text-2xl font-bold text-white">{formation.course_ids.length}</div>
                <div className="text-xs text-gray-400">Cursos Inclusos</div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-center">
                <Percent className="text-red-500 mx-auto mb-2" size={24} />
                <div className="text-2xl font-bold text-green-400">{formation.savings_percentage}%</div>
                <div className="text-xs text-gray-400">Economia</div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-center">
                <TrendingUp className="text-red-500 mx-auto mb-2" size={24} />
                <div className="text-lg font-bold text-white line-through opacity-50">
                  {fmt(formation.individual_total, { digits: 0 })}
                </div>
                <div className="text-xs text-gray-400">Valor Individual</div>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 bg-zinc-900">
                <TabsTrigger value="sobre" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
                  Sobre
                </TabsTrigger>
                <TabsTrigger value="conteudo" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
                  Conteúdo
                </TabsTrigger>
                <TabsTrigger value="cursos" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
                  Cursos
                </TabsTrigger>
              </TabsList>

              {/* Sobre */}
              <TabsContent value="sobre">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">Sobre a Formação</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <p className="text-gray-300">{formation.description}</p>
                    
                    {/* Pré-requisitos */}
                    {formation.prerequisites && (
                      <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                        <h4 className="text-white font-semibold mb-2 flex items-center">
                          <FileText size={18} className="mr-2 text-red-500" />
                          Pré-requisitos
                        </h4>
                        <p className="text-gray-300">{formation.prerequisites}</p>
                      </div>
                    )}

                    {/* Features */}
                    <div>
                      <h4 className="text-white font-semibold mb-3">O que está incluso:</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {formation.features?.map((feature, index) => (
                          <div key={`about-${feature}-${index}`} className="flex items-center space-x-2 text-gray-300">
                            <Check size={16} className="text-green-400 flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Conteúdo/Programa Didático */}
              <TabsContent value="conteudo">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">Programa Didático da Formação</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {formation.syllabus && formation.syllabus.length > 0 ? (
                      <div className="space-y-3">
                        {formation.syllabus.map((item, index) => (
                          <div
                            key={`syllabus-${item}-${index}`}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              item.startsWith('---') 
                                ? 'bg-red-900/30 border border-red-500/30' 
                                : 'bg-zinc-800'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              {!item.startsWith('---') && (
                                <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                  {index + 1}
                                </div>
                              )}
                              <span className={`${item.startsWith('---') ? 'text-red-400 font-semibold' : 'text-white'}`}>
                                {item.replace(/^---\s*/, '').replace(/\s*---$/, '')}
                              </span>
                            </div>
                            {!item.startsWith('---') && <BookOpen className="text-gray-400" size={18} />}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {includedCourses.map((course, index) => (
                          <div key={course.id} className="bg-zinc-800 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                  {index + 1}
                                </div>
                                <div>
                                  <h5 className="text-white font-semibold">{course.title}</h5>
                                  <p className="text-gray-400 text-sm">{course.duration} • {course.syllabus?.length || 0} tópicos</p>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/course/${course.id}`)}
                                className="text-red-400 hover:text-red-300"
                              >
                                Ver detalhes
                              </Button>
                            </div>
                            {course.syllabus && course.syllabus.length > 0 && (
                              <div className="ml-11 mt-2 space-y-1">
                                {course.syllabus.slice(0, 5).map((item, idx) => (
                                  <div key={`${course.id}-topic-${item}-${idx}`} className="flex items-center space-x-2 text-sm text-gray-400">
                                    <Check size={12} className="text-green-400" />
                                    <span>{item}</span>
                                  </div>
                                ))}
                                {course.syllabus.length > 5 && (
                                  <p className="text-xs text-gray-500 italic">
                                    ... e mais {course.syllabus.length - 5} tópicos
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Cursos Inclusos */}
              <TabsContent value="cursos">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">
                      {includedCourses.length} Cursos Inclusos nesta Formação
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {includedCourses.map((course, index) => (
                        <div
                          key={course.id}
                          className="bg-zinc-800 rounded-lg p-4 hover:border-red-500/50 border border-transparent transition-all cursor-pointer"
                          onClick={() => navigate(`/course/${course.id}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold">
                                {index + 1}
                              </div>
                              <div>
                                <h5 className="text-white font-semibold">{course.title}</h5>
                                <div className="flex items-center space-x-4 text-sm text-gray-400 mt-1">
                                  <span className="flex items-center">
                                    <Clock size={14} className="mr-1" />
                                    {course.duration}
                                  </span>
                                  <span className="flex items-center">
                                    <BookOpen size={14} className="mr-1" />
                                    {course.syllabus?.length || 0} tópicos
                                  </span>
                                </div>
                              </div>
                            </div>
                            <Badge className="bg-zinc-700">{course.code}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar - Pricing */}
          <div className="lg:col-span-1">
            <Card className="bg-zinc-900 border-zinc-800 sticky top-24">
              <CardHeader>
                <CardTitle className="text-white">Matricule-se</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Tabs defaultValue="ead_recorded" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 bg-zinc-800">
                    <TabsTrigger value="ead_recorded" className="data-[state=active]:bg-red-600 text-xs">
                      EaD
                    </TabsTrigger>
                    <TabsTrigger value="ead_live" className="data-[state=active]:bg-red-600 text-xs">
                      Ao Vivo
                    </TabsTrigger>
                    <TabsTrigger value="presential" className="data-[state=active]:bg-red-600 text-xs">
                      Presencial
                    </TabsTrigger>
                  </TabsList>

                  {Object.entries(formation.prices || {}).map(([modality, price]) => (
                    <TabsContent key={modality} value={modality} className="space-y-4 mt-4">
                      {modality === 'ead_recorded' ? (
                        <>
                          <div className="text-center">
                            <div className="text-sm text-gray-400">EaD Gravado</div>
                            <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
                              <span className="text-4xl font-bold text-white">{fmt(price)}</span>
                              <CurrencyToggle compact />
                            </div>
                            {currency === 'BRL' && (
                              <div className="text-sm text-gray-400 mt-1">
                                {formation.installments
                                  ? `${formation.installments.count}x de ${fmt(formation.installments.value)} + matrícula de ${fmt(formation.installments.enrollment_fee)}`
                                  : `ou 12x de ${fmt(price / 12)}`}
                              </div>
                            )}
                            {currency !== 'BRL' && (
                              <div className="inline-flex items-center gap-1.5 mt-2 text-xs text-blue-300 bg-blue-500/10 border border-blue-400/30 rounded-full px-3 py-1" data-testid="eur-international-note">
                                Pagamento internacional via PayPal
                              </div>
                            )}
                            <div className="text-green-400 font-semibold mt-2">
                              Economize {fmt((formation.individual_total || 0) - price, { digits: 0 })}
                            </div>
                          </div>
                          <Button
                            onClick={() => handleEnroll(modality)}
                            className="w-full bg-red-600 hover:bg-red-700 text-white"
                          >
                            Matricular Agora
                          </Button>
                        </>
                      ) : modality === 'presential' ? (
                        <div className="text-center py-8">
                          <Badge className="bg-zinc-700 text-gray-200 text-lg px-6 py-2">EM BREVE</Badge>
                        </div>
                      ) : (
                        <div className="text-center space-y-3">
                          <div className="text-sm text-gray-400">Elite (Ao Vivo)</div>
                          <div className="text-2xl font-bold text-white">Valores sob consulta</div>
                          <p className="text-gray-400 text-sm">
                            Esta modalidade tem condições personalizadas. Fale com um de nossos consultores.
                          </p>
                          <a
                            href="https://meet.brevo.com/alexander-justi/reuniao-video-conferencia"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                            data-testid="formation-schedule-meet-btn"
                          >
                            <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
                              <CalendarClock className="mr-2" size={16} />
                              Agendar reunião online
                            </Button>
                          </a>
                          <a
                            href={`https://wa.me/5561995792667?text=${encodeURIComponent(`Olá! Tenho interesse na ${formation.title} (modalidade Ao Vivo) e gostaria de conversar com um consultor.`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <Button variant="outline" className="w-full border-green-600 text-green-500 hover:bg-green-600 hover:text-white">
                              <MessageCircle className="mr-2" size={16} />
                              Falar no WhatsApp
                            </Button>
                          </a>
                        </div>
                      )}
                    </TabsContent>
                  ))}
                </Tabs>

                {/* Benefits */}
                <div className="space-y-2 pt-4 border-t border-zinc-700">
                  {formation.features?.slice(0, 4).map((feature, index) => (
                    <div key={`benefit-${feature}-${index}`} className="flex items-center space-x-2 text-sm text-gray-300">
                      <Check size={14} className="text-green-400" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </div>
  );
};

export default FormationDetailsPage;
